import { TrashIcon } from '@phosphor-icons/react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@shared/ui';
import { Modal } from '@shared/ui/Modal/Modal';

import styles from './Searches.module.css';

interface DeleteSearchDialogProps {
  open: boolean;
  title: string | null;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Подтверждение удаления запроса (FE-152, MVP_PRD §20).
 *
 * Текст обязан сказать обе вещи: что удаляется (запрос, диалог, результаты) и
 * что **переговоры сохранятся**. Второе — не любезность: письма продавцу уже
 * отправлены, удаление запроса их не отменяет, и пользователь должен понимать
 * это до нажатия, а не после.
 *
 * Действие необратимое, поэтому оптимистичного UI нет, а пока запрос
 * выполняется, окно не закрывается по Esc и клику по подложке.
 */
export function DeleteSearchDialog({
  open,
  title,
  pending,
  onCancel,
  onConfirm,
}: DeleteSearchDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const textId = useId();

  return (
    <Modal
      open={open}
      onClose={onCancel}
      labelledBy={titleId}
      describedBy={textId}
      dismissible={!pending}
    >
      <div className={styles.dialog}>
        <h2 id={titleId} className={styles.dialogTitle}>
          {t('searches.delete.title', { title: title ?? t('searches.untitled') })}
        </h2>
        <p id={textId} className={styles.dialogText}>
          {t('searches.delete.text')}
        </p>
        <p className={styles.dialogText}>{t('searches.delete.negotiations')}</p>
        <div className={styles.dialogActions}>
          <Button variant="secondary" onClick={onCancel} disabled={pending}>
            {t('searches.delete.cancel')}
          </Button>
          {/* Деструктивное действие — бордовым контуром, без заливки: кнопку,
              которая что-то ломает, нельзя спутать с главной (DESIGN_SYSTEM §5.1). */}
          <Button
            variant="danger"
            onClick={onConfirm}
            disabled={pending}
            iconEnd={<TrashIcon size={16} />}
          >
            {pending ? t('searches.delete.pending') : t('searches.delete.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
